import {Form, Formik, FormikProps} from 'formik';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    Button,
    DataWrapper,
    PasswordInputFormik,
    ToggleFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {ValidationCode} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {apiUtils, dataUtils} from 'utils';

import {OpenAIPropertiesFields} from '../types';

const FIELDS = {
    ENABLED: 'enabled',
    API_KEY: 'apiKey'
};

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<OpenAIPropertiesFields>;

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.CHAT_GPT.OPEN_AI_PROPERTIES;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FormTemplate: React.FC<FormTemplateProps> = ({loading, initialValues, submitForm, isValid, values}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-mt-2' defaultExpanded>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-ts-h5-bold'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        <ToggleFormik
                            name={FIELDS.ENABLED}
                            label={t(T_KEYS.LABELS.ENABLED)}
                            className='tw-my-4'
                        />
                        <PasswordInputFormik
                            name={FIELDS.API_KEY}
                            label={t(T_KEYS.LABELS.API_KEY)}
                            className='tw-w-full'
                            required={values.enabled}
                            autoComplete='one-time-code'
                        />
                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isValid}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type OpenAIPropertiesProps = {
    loading: boolean;
    initialValues: OpenAIPropertiesFields;
    onSubmit: (values: OpenAIPropertiesFields) => void;
};

export const OpenAIProperties: React.FC<OpenAIPropertiesProps> = ({
    loading, initialValues, onSubmit
}) => {
    const {t} = useTranslation();
    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.API_KEY]: Yup.string().when(FIELDS.ENABLED, {
            is: true,
            then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        }),
    }), [t]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={onSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    loading={loading}
                />
            )}
        </Formik>
    );
};